import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Copy,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { DocumentEditorApi } from "./useDocumentEditor";

interface BlockActionsProps {
  /** The editor whose selected block the actions target. */
  editor: DocumentEditorApi;
  /** Blocks editing (an image upload is in flight). */
  disabled: boolean;
}

/**
 * The toolbar's block operations: move, duplicate, and delete for the selected
 * block. They disable together when nothing is selected; an open text session's
 * block is still the selection, so the first click works (mousedown keeps the
 * editor focused, then the action commits the session before mutating). The
 * repeatable header and footer keep only Delete, since there is exactly one of
 * each outside the document flow and moving or duplicating them means nothing.
 * A table cell cannot be deleted - a removed cell would hole its row's grid -
 * so Delete disables for it.
 * A move steps the block among its siblings, which for a table column runs left
 * to right rather than down the page, so the arrows follow the direction the
 * block actually travels.
 * @param editor - The editor whose selected block the actions target
 * @param disabled - Blocks editing
 * @returns The block action buttons
 */
export function BlockActions({ editor, disabled }: BlockActionsProps) {
  const block = editor.selection;
  const deleteDisabled = disabled || block === null || block.rendersAsTableCell;
  const arrangeDisabled = disabled || block === null || block.isRepeatableBlock;
  const movesSideways = block?.tagName === "fp-column";
  const BackIcon = movesSideways ? ArrowLeft : ArrowUp;
  const ForwardIcon = movesSideways ? ArrowRight : ArrowDown;

  const act = (action: () => void) => () => {
    editor.commitTextEdit();
    action();
  };

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        title={movesSideways ? "Move left" : "Move up"}
        aria-label={movesSideways ? "Move left" : "Move up"}
        disabled={arrangeDisabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={act(() => editor.moveSelected("up"))}
      >
        <BackIcon className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        title={movesSideways ? "Move right" : "Move down"}
        aria-label={movesSideways ? "Move right" : "Move down"}
        disabled={arrangeDisabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={act(() => editor.moveSelected("down"))}
      >
        <ForwardIcon className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        title="Duplicate"
        aria-label="Duplicate"
        disabled={arrangeDisabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={act(editor.duplicateSelected)}
      >
        <Copy className="size-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="size-8 text-destructive"
        title="Delete (Del)"
        aria-label="Delete"
        disabled={deleteDisabled}
        onMouseDown={(event) => event.preventDefault()}
        onClick={act(editor.deleteSelected)}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );
}
